import { forwardRef } from "react";
import { Link, useTheme } from "@mui/material";
import { getDomain } from "tldts";
import { usePublisherSearch } from "../hooks/publisher-search.hook";
import { logEvent } from "services/amplitude.service";
import { useDispatch } from "react-redux";
import { setBoclipsPopup, setBoclipsVideoId } from "context/features/dataSlice";
import useLocalStorage from "@rehooks/local-storage";
import { useSelector } from "react-redux";
import { murmurhash3_32_gc } from "utils/string.util";
import { useTrackedArticles } from "../hooks";
import { devViewPdf } from "config";

const AFP_PATH = "/afp-article/";

const OFFICE_EXTENSIONS = [".doc", ".docx", ".ppt", ".pptx", ".xls", ".xlsx"];

function getDocumentPreviewUrl(url) {
  const lower = url.toLowerCase().split("?")[0];
  if (OFFICE_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`;
  }
  return null;
}

const SearchResultLink = forwardRef(function SearchResultLink(
  { url, trackingCat, trackingOptions, children, sx, ...props },
  ref
) {
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const { getPublisherToken } = usePublisherSearch();

  const { addClickedLink, hasBeenClicked } = useTrackedArticles(url);

  async function handleClick(event) {
    const url = event.currentTarget.href;
    const domain = getDomain(url)?.toLowerCase();
    const requiresTokenizedUrl =
      domain === "businessinsider.com" || domain === "washingtonpost.com";
    const requiresUrlRewrite =
      requiresTokenizedUrl ||
      domain === "foreignpolicy.com" ||
      url.includes(AFP_PATH);

    if (domain === "boclips.com") {
      event.preventDefault();
      dispatch(setBoclipsVideoId(url.split("/").pop()));
      dispatch(setBoclipsPopup(true));
      return false;
    }

    const previewUrl = devViewPdf ? getDocumentPreviewUrl(url) : null;
    if (previewUrl) {
      event.preventDefault();
      if (!hasBeenClicked) addClickedLink(url);
      if (trackingCat != null && trackingOptions != null) {
        logEvent(trackingCat, trackingOptions);
      }
      window.open(previewUrl, "_blank");
      return false;
    }

    if (!hasBeenClicked) {
      addClickedLink(url);
    }

    if (!requiresUrlRewrite) {
      if (trackingCat != null && trackingOptions != null) {
        logEvent(trackingCat, trackingOptions);
      }
      return true;
    }

    event.preventDefault();

    const articleWindow = requiresTokenizedUrl
      ? window.open("about:blank", "_blank")
      : null;
    if (articleWindow) {
      articleWindow.opener = null;
    }

    let newUrl = url;
    switch (domain) {
      case "businessinsider.com":
      case "washingtonpost.com":
        try {
          newUrl = await getPublisherToken(url);
        } catch {}
        break;
      case "foreignpolicy.com":
        newUrl += "?auth=sooth";
        break;
      default:
        if (url.includes(AFP_PATH)) {
          newUrl = `${window.location.origin}${AFP_PATH}${url
            .split("/")
            .pop()}`;
        }
        break;
    }

    if (articleWindow) {
      articleWindow.location.replace(newUrl);
    } else {
      window.open(newUrl, "_blank");
    }

    if (trackingCat != null && trackingOptions != null) {
      logEvent(trackingCat, trackingOptions);
    }
    return false;
  }
  return (
    <Link
      ref={ref}
      href={url}
      target="_blank"
      onClick={handleClick}
      sx={{
        color: palette.text.primary,
        ...sx,
      }}
      {...props}
    >
      {children}
    </Link>
  );
});

export default SearchResultLink;
